import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { apiFetch } from 'app/javascript/services/api';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import ProfileAvatar from 'app/javascript/components/shared/ProfileAvatar';
import BeltImage from 'app/javascript/components/shared/BeltImage';
import { ROUTES } from 'app/javascript/constants/routes';

interface Belt {
  id: number;
  rank: string;
  belt_received_at: string | null;
  confirmed: boolean;
}

interface ProfileSummary {
  id: number;
  name: string;
  nickname: string | null;
  slug: string;
  photo_url: string | null;
  current_belt: Belt | null;
}

interface PagyMetadata {
  page: number;
  pages: number;
  count: number;
  next: number | null;
  prev: number | null;
}

export default function ProfilesList() {
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagy, setPagy] = useState<PagyMetadata | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProfiles = useCallback(async (searchTerm: string, pageNum: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pageNum) });
      if (searchTerm.trim()) params.set('search', searchTerm.trim());

      const response = await apiFetch(`/api/public/profiles?${params}`);
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles);
        setPagy(data.pagy);
      }
    } catch (error) {
      console.error('Failed to fetch profiles', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles(search, page);
  }, [page, fetchProfiles]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchProfiles(value, 1);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      {/* Background Effects */}
      <div
        className="fixed inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #fbbf24 1px, transparent 1px),
            linear-gradient(180deg, #fbbf24 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
          <h1 className="font-bebas text-4xl">Atletas</h1>

          <div className="relative w-full md:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-500" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-slate-700/50 rounded-xl leading-5 bg-slate-900/50 text-slate-300 placeholder-slate-500 focus:outline-none focus:bg-slate-900 focus:ring-1 focus:ring-yellow-400/50 focus:border-yellow-400/50 sm:text-sm transition-all"
              placeholder="Buscar atleta..."
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20 text-slate-500">Carregando atletas...</div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            {search.trim() ? 'Nenhum atleta encontrado.' : 'Nenhum atleta cadastrado.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {profiles.map((profile) => {
                const rank = profile.current_belt?.rank || 'white';
                return (
                  <Link
                    key={profile.id}
                    to={ROUTES.PUBLIC_PROFILE}
                    params={{ slug: profile.slug }}
                    className="group relative flex flex-col items-center p-6 bg-slate-800/40 border border-slate-700/50 rounded-2xl hover:bg-slate-800/60 hover:border-yellow-400/30 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="relative mb-6">
                      <ProfileAvatar
                        photoUrl={profile.photo_url}
                        name={profile.name}
                        rank={rank}
                        size="sm"
                      />

                      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 shadow-lg rounded-lg bg-slate-900/80 backdrop-blur-sm p-1 border border-slate-700/50">
                        <BeltImage rank={rank} size="xs" />
                      </div>
                    </div>

                    <div className="flex flex-col items-center mt-2">
                      {profile.nickname && <p className="text-xs text-slate-400 font-medium text-center line-clamp-1 mb-0.5">&quot;{profile.nickname}&quot;</p>}
                      <h3 className="text-base sm:text-lg font-bold text-white text-center group-hover:text-yellow-400 transition-colors line-clamp-2 break-words">
                        {profile.name}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {pagy && pagy.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!pagy.prev}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-white hover:border-yellow-400/30 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:border-slate-700/50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">Anterior</span>
                </button>

                <span className="px-4 py-2 text-sm text-slate-400">
                  {pagy.page} de {pagy.pages}
                </span>

                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!pagy.next}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-white hover:border-yellow-400/30 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:border-slate-700/50"
                >
                  <span className="text-sm font-medium hidden sm:inline">Próximo</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}
